/**
 * Khaime SDK
 * Enterprise-grade embeddable commerce components
 *
 * @version 2.0.0
 * @license MIT
 */

// Core SDK
export { KhaimeSDK, VERSION } from './core/sdk';
export type { default as KhaimeSDKType } from './core/sdk';

// Components
export { KhaimeStorefrontElement } from './components/storefront/khaime-storefront';
export { KhaimeProductCardElement } from './components/storefront/khaime-product-card';
export { KhaimePayButtonElement } from './components/checkout/khaime-pay-button';
export { KhaimeCartPageElement } from './components/cart/khaime-cart-page';

// Base component for extending
export { KhaimeBaseElement, ComponentRegistry, BASE_STYLES } from './components/base';

// Types
export type {
  // Config types
  SDKType,
  SDKConfig,
  Theme,
  ThemeColors,
  Typography,
  Layout,
  Effects,
  Features,
  // Product types
  Product,
  ProductType,
  ProductVariant,
  ProductSubscription,
  ProductQueryOptions,
  ProductsResponse,
  // Event types
  SDKCallbacks,
  KhaimeEventMap,
  ProductClickEventDetail,
  AddToCartEventDetail,
  PaymentSuccessEventDetail,
  PaymentErrorEventDetail,
  CartUpdatedEventDetail,
  // Cart types
  LocalCartItem,
  CartState,
  CartApiResponse,
  // Options types
  SDKOptions,
  StorefrontOptions,
  CheckoutOptions,
  PayButtonOptions,
  PricingOptions,
  BootstrapResponse,
  CheckoutSession,
  CurrencyChoice,
} from './types';

// Utilities
export { formatPrice, getProductBadge } from './types';
export { ApiClient, getApiClient } from './utils/api-client';
export { CartManager } from './utils/cart-manager';
export { Cache, getCache, CacheKeys, CacheTTL } from './utils/cache';
export { configToCssVars, applyConfigToHost } from './utils/css-vars';
export { escapeHtml, debounce, throttle } from './utils/dom';

// Defaults
export { DEFAULT_CONFIG, DEFAULT_THEME_COLORS } from './types';

// Global export for IIFE build
import { KhaimeSDK } from './core/sdk';

/**
 * Factory wrapper for script-tag / WordPress usage.
 * Supports both:
 *   Khaime('api_key')              → returns SDK instance (WordPress shortcode pattern)
 *   Khaime.init({ merchantId })    → class-style init
 *   Khaime.storefront({ ... })     → static convenience methods
 */
function createKhaimeFactory() {
  function Khaime(apiKey: string): KhaimeSDK {
    return KhaimeSDK.init({ apiKey, merchantId: apiKey });
  }

  // Copy all static methods/properties from KhaimeSDK onto the factory function
  Object.getOwnPropertyNames(KhaimeSDK).forEach((key) => {
    if (key !== 'prototype' && key !== 'length' && key !== 'name') {
      (Khaime as any)[key] = (KhaimeSDK as any)[key];
    }
  });

  // Preserve prototype chain for instanceof checks
  Khaime.prototype = KhaimeSDK.prototype;

  return Khaime;
}

/**
 * Confirm a payment using the Khaime Payment Element
 * This delegates to KhaimeSDK.confirmPayment which supports both modal and inline modes
 *
 * Usage:
 *   const result = await Khaime.confirmPayment({
 *     secret: 'pi_xxx_secret_yyy',
 *     publishable_key: 'pk_xxx',
 *     stripe_account_id: 'acct_xxx', // optional
 *     amount: 5000, // amount in cents
 *     currency: 'USD',
 *     display: 'inline', // 'modal' (default) or 'inline'
 *     container: 'payment-container', // required for inline mode
 *   });
 */
const confirmPayment = KhaimeSDK.confirmPayment.bind(KhaimeSDK);

// Expose on window for script tag usage
if (typeof window !== 'undefined') {
  const factory = createKhaimeFactory();
  // Add confirmPayment as a static method
  (factory as any).confirmPayment = confirmPayment;
  (window as any).Khaime = factory;
  (window as any).KhaimeSDK = KhaimeSDK;
}

export { confirmPayment };
export default KhaimeSDK;
